import {
  ApolloProvider,
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  fromPromise,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { refreshToken as fetchRefreshToken } from "queries/auth";
import React, { ReactNode } from "react";

const authLink = setContext(() => {
  const token = localStorage.getItem("access-token");
  return {
    headers: {
      Authorization: token,
    },
  };
});

const errorLink = onError(
  // eslint-disable-next-line consistent-return
  ({ graphQLErrors, networkError, forward, operation }) => {
    if (graphQLErrors) {
      const isUnauthenticated = graphQLErrors.some(
        (err) => err.extensions?.code === "UNAUTHENTICATED"
      );
      const oldRefreshToken = localStorage.getItem("refresh-token");

      if (isUnauthenticated && oldRefreshToken) {
        return fromPromise(
          fetchRefreshToken({
            token: oldRefreshToken,
          }).catch(() => {
            // Clear tokens on refresh failure
            localStorage.removeItem("access-token");
            localStorage.removeItem("refresh-token");
            localStorage.removeItem("id-token");

            /*
             * Force page reload to login page if not already there
             * This clears all React state including AuthContext
             */
            setTimeout(() => {
              if (window.location.pathname !== "/") {
                window.location.href = "/";
              }
            }, 0);

            // Return null to stop the promise chain
            return null;
          })
        )
          .filter((result) => Boolean(result))
          .flatMap((result) => {
            if (!result?.data) {
              // Clear tokens on refresh failure
              localStorage.removeItem("access-token");
              localStorage.removeItem("refresh-token");
              localStorage.removeItem("id-token");

              // Force page reload to login page if not already there
              setTimeout(() => {
                if (window.location.pathname !== "/") {
                  window.location.href = "/";
                }
              }, 0);

              throw new Error("Refresh token failed");
            }

            const { accessToken, refreshToken, idToken } =
              result.data.refreshToken;

            localStorage.setItem("access-token", accessToken);
            localStorage.setItem("refresh-token", refreshToken);
            localStorage.setItem("id-token", idToken);

            const oldHeaders = operation.getContext().headers as Record<
              string,
              string
            >;

            operation.setContext({
              headers: {
                ...oldHeaders,
                Authorization: accessToken,
              },
            });

            return forward(operation);
          });
      }

      // Handle unauthenticated when no refresh token is available
      if (isUnauthenticated && !oldRefreshToken) {
        // Clear any remaining tokens
        localStorage.removeItem("access-token");
        localStorage.removeItem("refresh-token");
        localStorage.removeItem("id-token");

        // Force page reload to login page if not already there
        setTimeout(() => {
          if (window.location.pathname !== "/") {
            window.location.href = "/";
          }
        }, 0);
      }
    }

    if (networkError && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log(`[Network error]: ${networkError.message}`);
    }
  }
);

const httpLink = createHttpLink({
  uri: process.env.NEXT_PUBLIC_BACKEND_URL,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const client = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) as unknown as ApolloClient<any>;

interface ApolloClientProviderProps {
  children: ReactNode;
}

export const ApolloClientProvider: React.FC<ApolloClientProviderProps> = ({
  children,
}) => <ApolloProvider client={client}>{children}</ApolloProvider>;
